"use client";

import { useEffect, useState } from "react";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useCanAnimate } from "@/hooks/useCanAnimate";

interface AmbientGradientsProps {
  className?: string;
  paused?: boolean;
  isLightMode?: boolean;
  isNeutral?: boolean; // True when in hero/contact with no color theme
  isLoading?: boolean; // True during loader - starts at opacity 0, GSAP animates in
}

export function AmbientGradients({ className = "", paused = false, isLightMode = false, isNeutral = false, isLoading = false }: AmbientGradientsProps) {
  const [mounted, setMounted] = useState(false);
  const isMobile = useIsMobile();
  const canAnimate = useCanAnimate();

  // Pause animations when motion is off OR when explicitly paused (case study open)
  const shouldPause = paused || !canAnimate;

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  // On mobile, render a simplified single gradient without blur for performance
  if (isMobile) {
    const blobColor = "var(--theme-base-rgb)";
    const opacity = isLightMode ? 0.08 : 0.15;
    return (
      <div
        data-ambient-gradients
        className={`fixed inset-0 overflow-hidden pointer-events-none ${className}`}
        style={{
          opacity: isLoading ? 0 : (paused ? 0 : 1),
          transform: isLoading ? "scale(0.96)" : "scale(1)",
          transition: isLoading ? "none" : "opacity 0.5s ease-out",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse 80% 60% at 50% 40%, rgba(${blobColor}, ${opacity}) 0%, transparent 70%)`,
          }}
        />
      </div>
    );
  }

  const blobColor = "var(--theme-base-rgb)";
  const primaryOpacity = isLightMode ? 0.15 : 0.5;
  const secondaryOpacity = isLightMode ? 0.1 : 0.3;

  return (
    <div
      data-ambient-gradients
      className={`fixed inset-0 overflow-hidden pointer-events-none ${className}`}
      style={{
        opacity: isLoading ? 0 : (paused ? 0 : 1),
        transform: isLoading ? "scale(0.96)" : "scale(1)",
        transition: isLoading ? "none" : "opacity 0.5s ease-out",
      }}
    >
      {/* Primary blob - top left */}
      <div
        className="absolute w-[60vw] h-[60vw] max-w-[800px] max-h-[800px] rounded-full"
        style={{
          background: `radial-gradient(circle, rgba(${blobColor}, ${primaryOpacity}) 0%, rgba(${blobColor}, 0) 70%)`,
          filter: "blur(50px)",
          animation: "float1 10s ease-in-out infinite",
          animationPlayState: shouldPause ? "paused" : "running",
          top: "0%",
          left: "0%",
          willChange: shouldPause ? "auto" : "transform",
          contain: "layout style paint",
        }}
      />

      {/* Secondary blob - center */}
      <div
        className="absolute w-[50vw] h-[50vw] max-w-[600px] max-h-[600px] rounded-full"
        style={{
          background: `radial-gradient(circle, rgba(${blobColor}, ${secondaryOpacity}) 0%, rgba(${blobColor}, 0) 70%)`,
          filter: "blur(40px)",
          animation: "float2 8s ease-in-out infinite",
          animationPlayState: shouldPause ? "paused" : "running",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          willChange: shouldPause ? "auto" : "transform",
          contain: "layout style paint",
        }}
      />

      {/* Tertiary blob - bottom right */}
      <div
        className="absolute w-[50vw] h-[50vw] max-w-[600px] max-h-[600px] rounded-full"
        style={{
          background: `radial-gradient(circle, rgba(${blobColor}, ${secondaryOpacity}) 0%, rgba(${blobColor}, 0) 70%)`,
          filter: "blur(40px)",
          animation: "float3 12s ease-in-out infinite",
          animationPlayState: shouldPause ? "paused" : "running",
          bottom: "-10%",
          right: "-10%",
          willChange: shouldPause ? "auto" : "transform",
          contain: "layout style paint",
        }}
      />

      <style jsx>{`
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(15vw, 20vh); }
        }
        @keyframes float2 {
          0%, 100% { transform: translate(-50%, -50%); }
          50% { transform: translate(calc(-50% - 12vw), calc(-50% + 15vh)); }
        }
        @keyframes float3 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-20vw, -15vh); }
        }
      `}</style>
    </div>
  );
}
